<template>
  <fp-field :keys="$props">
    <v-hover>
      <VTextField
        slot-scope="{ hover }"
        :class="{
          dashed: dashed,
          'fp-box-input-style': box,
          'fp-input-outline': outline,
          'no-hint': !!value,
        }"
        :filled="box"
        :label="label"
        :id="id"
        :data-test="resolvedDataTest"
        :error="error"
        :success="success"
        :maxlength="maxLength"
        ref="fpInput"
        :dense="dense"
        :value="model"
        :color="color"
        :error-messages="errorMessages"
        @focus="focusCallback"
        @click="clickCallback"
        @keyup.enter="onEnter"
        @input="inputCallback"
        @change="changeCallback"
        @paste="pasteCallback"
        @blur="blurCallback($event.target.value)"
        :inputmode="isNumber ? 'numeric' : ''"
        :pattern="pattern ? pattern : isNumber ? '[0-9]*' : ''"
        :rules="rules"
        :validate-on-blur="validateOnBlur"
        :type="email ? 'email' : isNumber ? '' : (password || type === 'password') ? (showPassword ? 'text' : 'password') : type"
        :required="required"
        :outlined="outline"
        :placeholder="persistantPlaceholder || hover || isFocused ? placeholder : undefined"
        :disabled="disabled"
        :hide-details="hideDetails || (errorMessages.length === 0 && valid)"
        :suffix="suffix"
        :prefix="prefix"
        :clearable="
          !isNumber || !phone ? clearable && Boolean(value) && typeof value === 'string' && value.length > 0 : false
        "
        persistent-hint
        :auto-grow="!isNumber"
        :counter="counter"
        :loading="loading"
        :single-line="!dense ? singleLine : true"
        :multi-line="multiLine"
        rows="1"
        :autocomplete="autocomplete"
        :hint="description"
        :append-outer-icon="appendOuterIcon"
        :append-icon="(password || type === 'password') ? (showPassword ? 'visibility' : 'visibility_off') : appendIcon ?? undefined"
        :prepend-icon="prependIcon"
        :prepend-inner-icon="prependInnerIcon"
        @click:clear="clearCallback"
        @click:prepend-inner="$emit('click:prepend-inner')"
        @click:append="
          $emit('click:append');
          showPassword = !showPassword;
        "
      >
        <template v-slot:append>
          <slot name="append" />
        </template>
        <template v-slot:message="data">
          <slot name="message" :message="data.message" />
          <span v-if="!$scopedSlots.message">{{ data.message }}</span>
        </template>
        <template #append-outer>
          <slot name="append-outer" />
        </template>
      </VTextField>
    </v-hover>
  </fp-field>
</template>

<script>
import { debounce, isNaN } from 'lodash-es';
import { VHover, VTextField } from 'vuetify/lib';
import { conformToMask } from 'text-mask-core';
import { isPersonalIdNumberValid } from '@/components/fpComponents/services/countrySpecific/personalIdNumber';
import FpValidationMixin from '@/components/fpComponents/services/validationMixin';
import { unmaskText } from '@/components/fpComponents/services/unmask';
import FpField from '@/components/fpComponents/FpField/FpField.vue';
import EnvironmentService from '@/env/EnvironmentService';
import {resolveDataTest} from './useDataTest';

const FAKE_SLASH = '£';

const Patterns = {
  '#': { pattern: /\d/, },
  '/': { pattern: FAKE_SLASH, },
  '*': { pattern: /[0-9a-zA-Z]/, },
  '@': { pattern: /[a-zA-Z]/, },
};

export default {
  name: 'FpInput',
  components: { VTextField, VHover, FpField, },
  props: {
    noNumberMasking: {
      type: Boolean,
      default: false,
    },
    innerText: {
      type: String,
      default: undefined,
    },
    prependInnerIcon: {
      type: String,
      default: undefined,
    },
    appendOuterIcon: {
      type: String,
      default: undefined,
    },
    length: {
      type: [String, Number,],
      default: undefined,
    },
    minLength: {
      type: [String, Number,],
      default: undefined,
    },
    maxLength: {
      type: [String, Number,],
      default: undefined,
    },
    errorMessages: {
      default: () => [],
      type: [String, Array,],
    },
    counter: {
      default: undefined,
      type: [String, Boolean, Number,],
    },
    loading: {
      default: false,
      type: Boolean,
    },
    iban: {
      default: false,
      type: Boolean,
    },
    regon: {
      default: false,
      type: Boolean,
    },
    nip: {
      default: false,
      type: Boolean,
    },
    taxId: {
      default: false,
      type: Boolean,
    },
    int: {
      default: false,
      type: Boolean,
    },
    bban: {
      default: false,
      type: Boolean,
    },
    url: {
      default: false,
      type: Boolean,
    },
    dense: {
      default: false,
      type: Boolean,
    },
    box: {
      default: false,
      type: Boolean,
    },
    dirty: {
      default: undefined,
      type: Boolean,
    },
    dashed: {
      default: false,
      type: Boolean,
    },
    autofocusOnCreate: {
      default: false,
      type: Boolean,
    },
    success: {
      default: false,
      type: Boolean,
    },
    error: {
      default: false,
      type: Boolean,
    },
    outline: {
      default: true,
      type: Boolean,
    },
    placeholder: {
      default: '',
      type: String,
    },
    persistantPlaceholder: {
      default: false,
      type: Boolean,
    },
    autocomplete: {
      type: String,
    },
    prefix: {
      default: '',
      type: String,
    },
    appendIcon: {
      default: undefined,
    },
    prependIcon: {
      default: undefined,
      type: String,
    },
    suffix: {
      default: '',
      type: String,
    },
    singleLine: {
      type: Boolean,
      default: false,
    },
    multiLine: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    hideDetails: {
      default: 'auto',
      type: [Boolean, String,],
    },
    type: {
      default: 'text',
      type: String,
    },
    mask: {
      default: '',
      type: String,
    },
    preserveMask: {
      default: false,
      type: Boolean,
    },
    email: {
      default: false,
      type: Boolean,
    },
    validateOnBlur: {
      default: true,
      type: Boolean,
    },
    phone: {
      default: false,
      type: [Boolean, Number,],
    },
    personalId: {
      default: false,
      type: Boolean,
    },
    pesel: {
      default: false,
      type: Boolean,
    },
    date: {
      default: false,
      type: Boolean,
    },
    text: {
      default: false,
      type: Boolean,
    },
    debounceInput: {
      default: false,
      type: Boolean,
    },
    customErrorMessage: {
      type: String,
    },
    decimal: {
      default: undefined,
      type: [String, Number,],
    },
    pattern: {
      default: '',
      type: String,
    },
    branchBusinessCard: {
      default: false,
      type: Boolean,
    },
    flat: {
      default: false,
      type: Boolean,
    },
    color: {
      type: String,
      default: 'primary-text',
    },
    dataTest: {
      type: String,
      default: undefined,
    },
  },
  mounted() {
    if (this.autofocusOnCreate) {
      this.focus();
    }
  },
  mixins: [FpValidationMixin,],
  computed: {
    resolvedDataTest() {
      return resolveDataTest(this.dataTest, this.id);
    },
    vTextField: {
      get() {
        return this.$refs.fpInput.lazyValue;
      },
      set(newValue) {
        this.$refs.fpInput.lazyValue = newValue;
      },
    },
    model() {
      return this.preserveMask ? this.value : this.createModel(this.value);
    },
    isPersonalId() {
      return this.personalId || this.pesel;
    },
    isNumber() {
      return this.type === 'number';
    },
    isInt() {
      return this.int !== false;
    },
    pickedMaskLength() {
      return this.pickedMask.split('').filter((x) => Patterns[x]).length;
    },
    pickedMask() {
      if (!this.mask) {
        if (this.isNumber) return '';
        else if (this.regon) return '##############';
        else if (this.isPersonalId) {
          const domain = EnvironmentService.Environment.appDomain;
          const validator = isPersonalIdNumberValid[domain];
          return new Array(validator.length).fill('#').join('');
        } else if (this.date) return '##-##-####';
        else return this.mask;
      } else return this.mask;
    },
  },
  data() {
    return {
      isFocused: false,
      showPassword: false,
      isEnter: false,
    };
  },
  methods: {
    createModel(newValue, shouldDelete = false) {
      if (this.isNumber) {
        if (this.noNumberMasking) {
          return this.formatNumberToStringNumber(newValue);
        } else {
          const formattedNumber = this.formatNumberToStringNumber(newValue);
          return this.applyMaskToStringNumber(formattedNumber);
        }
      } else if (this.pickedMask && newValue) {
        const mask = this.pickedMask.split('').map((x) => {
          return Patterns[x] ? Patterns[x].pattern : x;
        });
        const conformedMask = conformToMask(newValue, mask, { guide: false, });
        let maskedValue = conformedMask.conformedValue.replaceAll(FAKE_SLASH, '/').substr(0, this.pickedMask.length);
        if (shouldDelete || (!this.preserveMask && maskedValue.endsWith(this.pickedMask[maskedValue.length - 1]))) {
          maskedValue = maskedValue.substr(0, maskedValue.length - 1);
        }
        return maskedValue;
      } else return newValue;
    },
    emitValue(key, value, event) {
      const isInput = key === 'input';
      const isBlur = key === 'blur';
      const isEnter = key === 'onEnter';
      if (typeof value !== 'object' || value === null) {
        if (this.isNumber) {
          if (this.noNumberMasking) {
            this.$refs.fpInput.lazyValue = this.formatNumberToStringNumber(value);
            value = this.isInt ? parseInt(value) : parseFloat(value);
            if (isNaN(value)) {
              value = null;
            }
          } else {
            const numberValue = this.formatNumberToStringNumber(value);
            this.$refs.fpInput.lazyValue = this.applyMaskToStringNumber(numberValue);
            value = this.isInt ? parseInt(numberValue) : parseFloat(numberValue);
            if (isNaN(value)) {
              value = null;
            }
          }
        } else if (this.pickedMask) {
          if (!this.preserveMask) {
            const unmaskedText = unmaskText(value, false);
            if (unmaskedText) {
              value = unmaskedText.substr(0, this.pickedMaskLength);
            }
            if (isInput) {
              this.$refs.fpInput.lazyValue = this.model;
            }
          } else {
            const shouldDelete = (value || '').length < (this.value || '').length;
            value = this.createModel(value, shouldDelete);
            if (isInput) {
              this.$refs.fpInput.lazyValue = value;
            }
          }
        }
        if (value === '') {
          value = null;
        }
        if (isBlur && this.nameValidation) {
          value = value?.trim();
          this.$refs.fpInput.lazyValue = value;
        }
        if (this.debounceInput) {
          this.debouncedEmitValue(key, value);
        } else if (isEnter) {
          this.$refs.fpInput.lazyValue = value;
          this.$emit('onEnter', event, value);
        } else {
          this.$emit(key, value);
        }
      }
    },
    applyMaskToStringNumber(stringValue) {
      if (stringValue) {
        if (this.mask) {
          return stringValue;
        }

        let [leftSide, rightSide,] = stringValue.split('.');
        leftSide = leftSide.replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1 ');
        return stringValue.includes('.')
          ? `${leftSide}.${this.decimal ? rightSide.substring(0, Number(this.decimal)) : rightSide || ''}`
          : leftSide;
      } else {
        return null;
      }
    },
    formatNumberToStringNumber(value) {
      if (value !== null) {
        value = String(value).replace(',', '.');
        let [leftSide, rightSide,] = value.split('.');
        let stringNumber = leftSide.replace(/[^\d]+/g, '');
        if (!this.isInt && value.includes('.')) {
          rightSide = rightSide.replace(/[^\d]+/g, '') || '';
          stringNumber = `${stringNumber}.${
            this.decimal ? rightSide.substring(0, Number(this.decimal)) : rightSide || ''
          }`;
        }
        return stringNumber;
      } else {
        return null;
      }
    },
    focus() {
      this.isFocused = true;
      setTimeout(() => this.$refs.fpInput.focus(), 50);
    },
    focusCallback() {
      this.isFocused = true;
      this.emitValue('focus', this.model);
    },
    blur() {
      this.isFocused = false;
      this.$refs.fpInput.blur();
    },
    blurCallback(value) {
      this.isFocused = false;
      if (this.pickedMask && this.validateOnBlur) {
        const unmaskedText = unmaskText(value);
        if (unmaskedText) {
          value = unmaskedText.substr(0, this.pickedMaskLength);
        }
      }
      if (!this.isEnter) {
      this.emitValue('blur', value);
      }
      this.isEnter = false;
    },
    pasteCallback(value) {
      this.$emit('paste', value);
    },
    inputCallback(value) {
      if (this.nameValidation) {
        value = this.replaceNameSpecialCharacters(value);
      }
      this.emitValue('input', value);
    },
    changeCallback(value) {
      this.emitValue('change', value);
    },
    onEnter(event) {
      if (this.$listeners.onEnter) {
        const inputElement = this.$refs.fpInput.$el;
        this.isEnter = true;
        this.emitValue('onEnter', event.target.value, event);
        inputElement.style.setProperty('--on-enter', 1);
        this.blur();
        requestAnimationFrame(() => {
          setTimeout(() => {
            inputElement.style.setProperty('--on-enter', 0);
          }, 100);
        });
      }
    },
    clickCallback(value) {
      this.isFocused = true;
      this.$emit('click', value);
    },
    clearCallback() {
      this.emitValue('input', null);
      this.$emit('click:clear');
    },
    debouncedEmitValue: debounce(function(key, value) {
      this.$emit(key, value);
    }, 800),
    replaceNameSpecialCharacters(input) {
      if (!input) {
        return input;
      }

      input = input.replace(/(^\s+|\s+$)|\s*([-' ])\s*/g, '$2');
      return input;
    },
  },
};
</script>

<style scoped lang="scss">
@property --on-enter {
  syntax: '<number>';
  inherits: true;
  initial-value: 0;
}

:deep(.v-text-field div.v-input__control) {
  transition: transform 0.1s ease-in-out;
  transform: translateY(calc(var(--on-enter) * 2px)) scale(calc(1 - var(--on-enter) * 0.005));
} 

>>> label::after {
  content: '' !important;
}
.dashed {
  ::v-deep fieldset {
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23333' stroke-width='2' stroke-dasharray='6%2c8' stroke-dashoffset='25' stroke-linecap='square'/%3e%3c/svg%3e");
  }
  &.theme--dark {
    ::v-deep fieldset {
      background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%23ffffff3d' stroke-width='2' stroke-dasharray='6%2c8' stroke-dashoffset='25' stroke-linecap='square'/%3e%3c/svg%3e");
      border: none;
    }
  }
}
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  -moz-appearance: none;
  margin: 0;
}
div :deep(input[type='number']) {
  -moz-appearance: textfield !important;
}
input {
  width: 100%;
}

>>> .v-input:not(.error--text) {
  .no-hint {
    ::v-deep .v-input__slot {
      margin-bottom: 0px !important;
    }

    ::v-deep.v-text-field__details {
      height: 0 !important;
      display: none !important;
    }
  }
}
:deep(.v-text-field__slot) {
  input:-internal-autofill-selected {
    &:hover, &:active, &:focus, & {
      -webkit-box-shadow: 0 0 0px 200px var(--token-primary-surface) inset;
      -webkit-text-fill-color: var(--token-core-onCore-highEmphasis) !important;
    }
  }
}
</style>
